import * as mui from "@material-ui/core";
import {
  createStyles,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Switch,
  Tab,
  TabProps,
  Tabs,
  TextField,
  withStyles
} from "@material-ui/core";
import DragHandleIcon from '@material-ui/icons/DragHandle';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import {Autocomplete} from "@material-ui/lab";
import * as React from "react";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  DraggingStyle,
  Droppable,
  DropResult,
  NotDraggingStyle
} from "react-beautiful-dnd";
import {Col, ControlLabel, FormControl, FormGroup, OverlayTrigger, Tooltip} from "react-bootstrap";
import {Alert, Button, Checkbox, Form, Modal, Radio, Row} from "react-bootstrap/lib";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {AddResponse} from "../api/InternalApi";
import {ADD_DATA_ADVANCED_OPTIONS_KEY, ADD_DATA_OTHER_OPTIONS_KEY, localRepository} from "../common/LocalRepository";
import {ChooseFileDialog} from "../common/ui/choosefolder/ChooseFileDialog";
import {ChooseFileFormSourceType, ChooseMode, ItemType} from "../common/ui/choosefolder/ChooseFileForm";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {Spinner} from "../common/ui/spinner/Spinner";
import {TabPanel} from "../common/ui/tab/TabPanel";
import {mapErrorToMessage} from "../common/util/ErrorMapper";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {SecurityForm} from "../security/SecurityForm";
import {Role} from "../user/model";
import {
  AddType,
  APOLLO_CATALOG_ROOT_FOLDER,
  EMPTY_OGC_CONNECTION,
  NewDataRoot,
  NewFileInfo,
  OGC_FILE_EXTENSION,
  OGCConnectionConfig,
  OgcServiceType
} from "./model";
import {DatabaseConnectionForm, DatabaseConnectionFormComponent} from "./DatabaseConnectionForm";
import {
  DatabaseType,
  DB_DRIVER,
  DB_FILE_EXTENSION,
  DB_PROTOCOL,
  DBConnectionConfig,
  EMPTY_ORACLE_DB_CONNECTION
} from "../settings/model";
import {DatabaseConnectionChooser} from "./DatabaseConnectionChooser";
import {FileList} from "../common/ui/choosefolder/FileList";
import {AccessRule} from "../model";
import {actions} from "./actions";
import {connect} from "react-redux";
import {FileUploadForm} from "../common/ui/upload/FileUploadForm";
import {DEFAULT_UPLOAD_OPTIONS, defaultCreateFormData} from "../common/ui/upload/WithFileUpload";
import {UploadOptions} from "../common/ui/upload/UploadOptions";
import {OgcConnectionForm, OgcConnectionFormComponent} from "./OgcConnectionForm";
import {ApolloApi} from "../api/ApolloApi";
import {RIAMap} from "../common/ui/riamap/RIAMap";
import {RESTRICT_NAVIGATION_BOUNDS} from "../common/ui/riamap/Previewer";
import {Feature} from "@luciad/ria/model/feature/Feature.js";
import InputGroup from "react-bootstrap/lib/InputGroup";
import InputGroupAddon from "react-bootstrap/lib/InputGroupAddon";
import InputGroupButton from "react-bootstrap/lib/InputGroupButton";
import ReactDOM from "react-dom";
import {
  convertSecurityFormAccessRulesToAccessRules, getPolygonPointsFromFeature, PermissionsType,
} from "../security/model";

const LABEL_COL_WIDTH = 3;
const CONTROL_COL_WIDTH = 8;
const HELP_COL = 1;

const AddOptionsTab = withStyles(() => {
  return createStyles({
    root: {
      minWidth: "20%",
    }
  });
})((props: TabProps) => <Tab {...props} />);

const mapDispatchToProps = (dispatch, _ownProps) => ({
  refresh: () => {
    return dispatch(actions.refreshPage());
  },
});

interface AddDataDialogDispatchProps {
  refresh: () => {};
}

export interface AddDataDialogProps {
  onSuccess?: (r: AddResponse) => void;
  onHide?: () => void;
  parentFolderId: string;
}

interface OtherOptionsState {
  addType: AddType;
  path?: string;
  localStorageVersion?: number;
}

interface AdvancedOptionsState {
  crawlReplicateDirStructure: boolean;
  crawlOnlySelectedExtensions: boolean;
  crawlFiltersActive: string[];
  crawlGeneratePyramids: boolean;
  crawlGenerateThumbnail: boolean;
  crawlMetadataParsers: string[];
  crawlMetadataParserStopAfterFirstSuccessful: boolean;
  crawlAllowDownloads: boolean;
  unmappedRoles: AccessRule[];
  localStorageVersion?: number;
}

enum AdvancedTabPanelValue {
  SETTINGS = "settings",
  SECURITY = "security",
  METADATA = "metadata",
}

const ADVANCED_TABS = [
  {
    value: AdvancedTabPanelValue.SETTINGS,
    label: "Settings",
    isHidden: () => false,
  },
  {
    value: AdvancedTabPanelValue.SECURITY,
    label: "Security",
    isHidden: () => false,
  },
  {
    value: AdvancedTabPanelValue.METADATA,
    label: "Metadata",
    isHidden: (state: AddDataDialogState) => {
      const {addType} = state;
      return (AddType.DATA_ROOT !== addType && AddType.UPLOAD !== addType &&
              AddType.FILES !== addType && AddType.S3 !== addType);
    }
  },
];

export interface AddDataDialogState extends AdvancedOptionsState {
  isExecuting: boolean;
  isSuccess: boolean;
  error: Error;
  crawlFiltersAvailable: string[];
  metadataParsersOptions: string[];
  defaultsOptions: AdvancedOptionsState;
  setAsDefaults: boolean;
  addType: AddType;
  path?: string;
  initialPath: string;
  files: string[];
  isDatabaseConnectionValid: boolean;
  isOgcConnectionValid: boolean;
  databaseConnection: DBConnectionConfig;
  isChooseFolderDialogOpen: boolean;
  showAdvancedOptions: boolean;
  usingDefaultOptions: boolean;
  activeTab: AdvancedTabPanelValue;
  mapDrawModeActive: boolean;
  mapEditModeActive: boolean;
  securitySpatialAreaCoordinates: number[];
  prevSecuritySpatialAreaCoordinates: number[];
  isSecurityLoading: boolean;
  isUserManagementActive: boolean;
  allRoles: Role[];
  droppedFiles: File[];
  uploadOptions: UploadOptions;
  ogcConnection: OGCConnectionConfig;
  parentFolderName: string;
  parentFolderId: string;
  crawlAccessRules: AccessRule[];
  s3Path: string;
}

const defaultState: AddDataDialogState = {
  isExecuting: false,
  error: null,
  isSuccess: false,
  crawlAccessRules: [],
  crawlAllowDownloads: true,
  crawlFiltersActive: [],
  crawlFiltersAvailable: [],
  crawlOnlySelectedExtensions: false,
  crawlGeneratePyramids: true,
  crawlGenerateThumbnail: true,
  crawlMetadataParserStopAfterFirstSuccessful: true,
  crawlMetadataParsers: [],
  crawlReplicateDirStructure: true,
  setAsDefaults: false,
  addType: AddType.DATA_ROOT,
  metadataParsersOptions: [],
  path: "",
  initialPath: "C:\\",
  files: [],
  databaseConnection: EMPTY_ORACLE_DB_CONNECTION,
  isDatabaseConnectionValid: true,
  isOgcConnectionValid: true,
  defaultsOptions: null,
  isChooseFolderDialogOpen: false,
  showAdvancedOptions: false,
  usingDefaultOptions: false,
  activeTab: ADVANCED_TABS[0].value,
  mapDrawModeActive: false,
  mapEditModeActive: false,
  securitySpatialAreaCoordinates: null,
  prevSecuritySpatialAreaCoordinates: null,
  isSecurityLoading: true,
  isUserManagementActive: null,
  allRoles: [],
  unmappedRoles: [],
  droppedFiles: [],
  uploadOptions: DEFAULT_UPLOAD_OPTIONS,
  ogcConnection: EMPTY_OGC_CONNECTION,
  parentFolderName: "",
  parentFolderId: "",
  s3Path: "",
}

const MESSAGES = defineMessages({
  chooseFolderDialogTitle: {
    id: "studio.add-data-dialog.choose-folder-dialog-title",
    defaultMessage: "Choose folder to add",
  },
  chooseFolderDialogButtonText: {
    id: "studio.add-data-dialog.choose-folder-dialog-button-text",
    defaultMessage: "Select",
  },
  today: {
    id: "studio.search-form.date-picker-today-label",
    defaultMessage: "Today",
  },
  addTypeDataRoot: {
    id: "studio.add-data-dialog.add-type.data-root",
    defaultMessage: "Data Root",
  },
  addTypeFiles: {
    id: "studio.add-data-dialog.add-type.files",
    defaultMessage: "Files",
  },
  addTypeDatabase: {
    id: "studio.add-data-dialog.add-type.database",
    defaultMessage: "Database",
  },
  addTypeUpload: {
    id: "studio.add-data-dialog.add-type.Upload",
    defaultMessage: "Upload",
  },
  addTypeOGC: {
    id: "studio.add-data-dialog.add-type.ogc",
    defaultMessage: "OGC",
  },
  addTypeS3: {
    id: "studio.add-data-dialog.add-type.s3",
    defaultMessage: "S3",
  },
  advancedOptions: {
    id: "studio.add-data-dialog.advanced-options",
    defaultMessage: "Advanced Options",
  },
  activeCrawlFilters: {
    id: "studio.add-data-dialog.active-crawl-filters",
    defaultMessage: "Active Crawl Filters",
  },
  parsers: {
    id: "studio.add-data-dialog.parsers",
    defaultMessage: "Parsers",
  },
  ogcWMS: {
    id: "studio.add-data-dialog.ogc-wms",
    defaultMessage: "WMS",
  },
  ogcWFS: {
    id: "studio.add-data-dialog.ogc-wfs",
    defaultMessage: "WFS",
  },
  ogcWCS: {
    id: "studio.add-data-dialog.ogc-wcs",
    defaultMessage: "WCS",
  },
  ogcWMTS: {
    id: "studio.add-data-dialog.ogc-wmts",
    defaultMessage: "WMTS",
  },
  unknownParent: {
    id: "studio.create-folder-dialog.unknown-parent",
    defaultMessage: "Unknown Parent",
  },
  HELP: {
    id: "studio.UI.help-alt",
    defaultMessage: "❔",
  },
});

class AddDataDialogComponent extends React.Component<AddDataDialogProps & InjectedIntlProps & WithApiProperties & AddDataDialogDispatchProps, AddDataDialogState> {
  private securityForm: any;
  private listItemPortalElement: HTMLDivElement;
  private databaseConnectionForm: DatabaseConnectionFormComponent;
  private ogcConnectionForm: OgcConnectionFormComponent;
  private mapInit: boolean;

  constructor(props) {
    super(props);
    this.state = defaultState;
    this.mapInit = false;

    const defaultOptionsState = localRepository.getOptions<AdvancedOptionsState>(ADD_DATA_ADVANCED_OPTIONS_KEY);
    if (defaultOptionsState) {
      this.state = Object.assign(this.state, defaultOptionsState, {
        showAdvancedOptions: true,
        usingDefaultOptions: true,
      });
    }
  }

  componentDidMount() {
    this.listItemPortalElement = document.createElement("div");
    this.listItemPortalElement.classList.add("add-data-dialog-list--item-portal");
    document.body.appendChild(this.listItemPortalElement);

    const {api} = this.props;
    const {usingDefaultOptions} = this.state;

    const otherOptions = localRepository.getOptions<OtherOptionsState>(ADD_DATA_OTHER_OPTIONS_KEY);
    if (otherOptions) {
      this.setState({isSecurityLoading: true, ...otherOptions});
    } else {
      this.setState({isSecurityLoading: true});
    }
    Promise.all([
      api.getCrawlAccessRules(),
      ApolloApi.getStudioSettings(),
      ApolloApi.getMetadataParsers(),
      api.isUserManagementActive(),
      api.getAllRoles()
    ])
        .then(([
                 crawlAccessRules,
                 studioSettings,
                 metadataParsersOptions,
                 isUserManagementActive,
                 allRoles
               ]) => {
          const newStateValues = {
            unmappedRoles: crawlAccessRules,
            isSecurityLoading: false,
            isUserManagementActive,
            allRoles,
            crawlFiltersAvailable: [...JSON.parse(studioSettings.data.values.crawlFiltersAvailable)],
            metadataParsersOptions,
          };
          if (usingDefaultOptions) {  //use values stored in Local Storage
            this.setState(newStateValues);
          } else { //use default values from the Server
            this.setState({
              ...newStateValues,
              crawlFiltersActive: [...JSON.parse(studioSettings.data.values.crawlFiltersActive)],
              crawlGeneratePyramids: studioSettings.data.values.crawlGeneratePyramids === "true",
              crawlGenerateThumbnail: studioSettings.data.values.crawlGenerateThumbnail === "true",
              crawlMetadataParserStopAfterFirstSuccessful: studioSettings.data.values.crawlMetadataParserStopAfterFirstSuccessful ===
                                                           "true",
              crawlMetadataParsers: [...JSON.parse(studioSettings.data.values.crawlMetadataParsers)],
              crawlReplicateDirStructure: studioSettings.data.values.crawlReplicateDirStructure === "true",
              crawlAccessRules
            });
          }
        });
  }

  componentDidUpdate() {
    this.updateParentDisplayName();
    if (!ADVANCED_TABS.find(t => t.value === this.state.activeTab).isHidden(this.state)) {
      return;
    }
    this.setState(prevState => ({
      activeTab: ADVANCED_TABS.find(t => !t.isHidden(prevState)).value,
    }));
  }

  componentWillUnmount() {
    document.body.removeChild(this.listItemPortalElement);
  }

  render() {
    return (
        <div>
          {this.renderModal()}
          {this.renderChooseFolderDialog()}
        </div>
    );
  }

  private closeDialog = () => {
    this.props.onHide();
  }

  private showChooseFolderDialog = () => {
    this.setState({isChooseFolderDialogOpen: true});
  }

  private closeChooseFileDialog = () => {
    this.setState({isChooseFolderDialogOpen: false});
  }

  private handleChooseDataRoot = (path: string, _type: ItemType) => {
    this.setState({path});
    return Promise.resolve();
  }

  private performAdd = () => {
    const {addType, isDatabaseConnectionValid, isOgcConnectionValid} = this.state;
    const {api} = this.props;

    if (addType === AddType.DATABASE && !isDatabaseConnectionValid) {
      this.databaseConnectionForm.showAllErrors();
      return;
    }
    if (addType === AddType.OGC && !isOgcConnectionValid) {
      this.ogcConnectionForm.showAllErrors();
      return;
    }

    let save: () => Promise<AddResponse>;
    if (addType === AddType.FILES) {
      save = () => api.addFiles(this.getFilesToAdd());
    }
    if (addType === AddType.DATA_ROOT) {
      save = () => api.createDataRoot(this.getNewDataRoot(false));
    }
    if (addType === AddType.DATABASE) {
      const {databaseConnection} = this.state;

      save = () => api.uploadVirtualFile(
          `${databaseConnection.connectionName}.${DB_FILE_EXTENSION[databaseConnection.type]}`,
          this.getDatabaseFileString(), {dataRoot: this.getNewDataRoot(false)});
    }

    if (addType === AddType.OGC) {
      const {ogcConnection} = this.state;

      save = () => api.uploadVirtualFile(
          `${ogcConnection.ogcConnectionName}.${OGC_FILE_EXTENSION[ogcConnection.ogcServiceType]}`,
          this.getOGCFileString(), {dataRoot: this.getNewDataRoot(false)});
    }

    if (addType === AddType.S3) {
      save = () => api.addFiles([this.getS3fileToAdd()]);
    }

    this.setState({isExecuting: true, error: null, isSuccess: false});
    return save().then((r) => {
      this.setState({isExecuting: false, error: null, isSuccess: true});
      this.saveToLocalStorage();
      this.closeDialog();
      this.props.onSuccess?.(r);
      this.props.refresh();
    }).catch((error) => {
      this.setState({isExecuting: false, error, isSuccess: false});
    });
  }

  private saveToLocalStorage = () => {
    const {crawlAccessRules} = this.state;
    if (this.state.setAsDefaults) {
      const advancedOptions = this.getAdvancedOptionsState();
      localRepository.setOptions(ADD_DATA_ADVANCED_OPTIONS_KEY,
          {...advancedOptions, crawlAccessRules});
    }
    localRepository.setOptions(ADD_DATA_OTHER_OPTIONS_KEY, this.getOtherOptionsState());
  }

  private getNewDataRoot(skipUpdateState: boolean): NewDataRoot {
    const {parentFolderId} = this.props;
    const metadataParsersSettings = this.getMetadataParserSettings();
    const generatePyramids = this.state.crawlGeneratePyramids;
    const generateThumbnail = this.state.crawlGenerateThumbnail;
    const crawlOnlySelectedExtensions = this.state.crawlOnlySelectedExtensions;
    const crawlAccessRules = this.getAccessRules();
    if (!skipUpdateState) {
      this.setState({crawlAccessRules})
    }

    return {
      rootPath: this.state.path,
      parent: parentFolderId && parentFolderId !== "0" ? parentFolderId : null,
      replicateDirectoryStructure: this.state.crawlReplicateDirStructure,
      allowDownloads: this.state.crawlAllowDownloads,
      thumbnail: generateThumbnail,
      pyramid: generatePyramids,
      metadata: metadataParsersSettings,
      crawlJob: {
        accessRuleTemplates: crawlAccessRules
      },
      crawlTheseExtensionsOnly: crawlOnlySelectedExtensions ? this.state.crawlFiltersActive : null
    } as NewDataRoot
  }

  private getMetadataParserSettings() {
    return {
      ...(this.state.crawlMetadataParsers.length === 0 ? undefined : {
        stopAtFirst: this.state.crawlMetadataParserStopAfterFirstSuccessful,
        metadataCrawlers: this.state.crawlMetadataParsers,
      })
    };
  }

  private getS3fileToAdd() {
    const {s3Path} = this.state;
    const {parentFolderId} = this.props;
    const metadataParsersSettings = this.getMetadataParserSettings();
    const accessRules = this.getAccessRules();
    const generatePyramids = this.state.crawlGeneratePyramids;
    const generateThumbnails = this.state.crawlGenerateThumbnail;

    return {
      parent: parentFolderId && parentFolderId !== "0" ? parentFolderId : null,
      filePath: s3Path,
      allowDownloads: this.state.crawlAllowDownloads,
      thumbnail: generateThumbnails,
      pyramid: generatePyramids,
      accessRules,
      metadata: metadataParsersSettings
    } as NewFileInfo
  }

  private getFilesToAdd() {
    const {files} = this.state;
    const {parentFolderId} = this.props;
    const metadataParsersSettings = this.getMetadataParserSettings();
    const accessRules = this.getAccessRules();
    const generatePyramids = this.state.crawlGeneratePyramids;
    const generateThumbnails = this.state.crawlGenerateThumbnail;

    const filesToAdd = []
    files.forEach((file) => {
      filesToAdd.push({
        parent: parentFolderId && parentFolderId !== "0" ? parentFolderId : null,
        filePath: file,
        allowDownloads: this.state.crawlAllowDownloads,
        thumbnail: generateThumbnails,
        pyramid: generatePyramids,
        accessRules,
        metadata: metadataParsersSettings
      } as NewFileInfo);
    });
    return filesToAdd;
  }

  private objectsEqual(o1: any, o2: any): boolean {
    const keys1 = Object.keys(o1);
    const keys2 = Object.keys(o2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      const areObjects = o1[key] !== null && typeof o1[key] === "object" &&
                         o2[key] !== null && typeof o2[key] === "object";
      if ((areObjects && !this.objectsEqual(o1[key], o2[key])) ||
          !areObjects && o1[key] !== o2[key]) {
        return false;
      }
    }
    return true;
  }

  private getAccessRules(): AccessRule[] {
    if (!this.securityForm) {  /* this.securityForm should always be valid because of hack, TODO: SecurityForm should use redux state instead */
      if (this.state.crawlAccessRules) {
        return this.state.crawlAccessRules.map((p) => {
          const rule: AccessRule = {
            role: p.role,
            permissions: p.permissions,
          }

          if (p.scaleRange) {
            rule["scaleRange"] = {
              minScaleDenominator: p.scaleRange.minScaleDenominator,
              maxScaleDenominator: p.scaleRange.maxScaleDenominator
            }
          }
          if (p.spatialRange) {
            rule["spatialRange"] = p.spatialRange;
          }
          return rule;
        });
      }
      return [{
        role: "ADMIN",
        permissions: [PermissionsType.READ, PermissionsType.UPDATE, PermissionsType.DELETE, PermissionsType.MANAGE]
      }] as AccessRule[];
    }

    const permissionsToSave = this.securityForm.getWrappedInstance().getResult().permissionsToSave;
    const newCrawlAccessRules = convertSecurityFormAccessRulesToAccessRules(permissionsToSave);

    if (!this.objectsEqual(this.state.crawlAccessRules, newCrawlAccessRules)) {
      this.setState({crawlAccessRules: newCrawlAccessRules});
    }

    return newCrawlAccessRules;
  }

  private getOGCFileString() {
    const {ogcConnection} = this.state;
    let ogcFile = `url=${ogcConnection.ogcUrl}\n`;

    switch (ogcConnection.ogcServiceType) {
    case OgcServiceType.WMS:
    case OgcServiceType.WMTS:
      ogcFile += `layer=${ogcConnection.ogcLayer}`;
      break;
    case OgcServiceType.WCS:
      ogcFile += `coverage=${ogcConnection.ogcCoverage}`;
      break;
    case OgcServiceType.WFS:
      ogcFile += `featureType=${ogcConnection.ogcFeatureType}`;
      break;
    }
    return ogcFile;
  }

  private getJdbcString() {
    const {databaseConnection} = this.state;
    const type = databaseConnection.type;

    let jdbc = `${DB_PROTOCOL[type]}${databaseConnection.serverName}`;
    switch (type) {
    case DatabaseType.POSTGIS:
      jdbc += `:${databaseConnection.serverPort}/${databaseConnection.postgreSqlDatabase}`;
      break;
    case DatabaseType.SQLSERVER:
      jdbc += databaseConnection.mssqlInstanceName !== "" ? `\\${databaseConnection.mssqlInstanceName.trim()}`
                                                          : `:${databaseConnection.serverPort}`;
      jdbc += `;databaseName=${databaseConnection.mssqlDatabase}`;
      break;
    case DatabaseType.ORACLE:
      jdbc += `:${databaseConnection.serverPort}`;
      if (databaseConnection.oracleSid !== "") {
        jdbc += `:${databaseConnection.oracleSid}`;
      } else {
        jdbc += `/${databaseConnection.oracleServiceName}`;
      }
    }
    return jdbc;
  }

  private getDatabaseFileString() {
    const {databaseConnection} = this.state;
    const type = databaseConnection.type;

    let databaseFile = `# Properties file for ${type}.\n` +
                       "\n" +
                       "# The basic database connection properties.\n" +
                       `driver   = ${DB_DRIVER[type]}\n` +
                       `url      = ${this.getJdbcString()}\n` +
                       `user     = ${databaseConnection.serverUsername}\n` +
                       `password = ${databaseConnection.serverPassword}\n` +
                       "\n" +
                       "# The data of interest.\n" +
                       `table         = ${databaseConnection.databaseTable}\n` +
                       `spatialColumn = ${databaseConnection.spatialColumn}\n` +
                       "\n";

    databaseFile += "# The feature columns to be decoded.\n";
    for (let i = 0; i < databaseConnection.databaseFeatures.length; i++) {
      databaseFile += `featureNames.${i} = ${databaseConnection.databaseFeatures[i].featureName}\n`
    }

    databaseFile += "\n" +
                    "# Optional display names.\n";
    for (let i = 0; i < databaseConnection.databaseFeatures.length; i++) {
      if (databaseConnection.databaseFeatures[i].displayName &&
          databaseConnection.databaseFeatures[i].displayName.trim().length > 0) {
        databaseFile += `featureDisplayNames.${i} = ${databaseConnection.databaseFeatures[i].displayName}\n`;
      }
    }

    databaseFile += "\n" +
                    "# The index of the feature that can be used as primary key.\n" +
                    `primaryFeatureIndex = ${databaseConnection.databaseFeatures.findIndex(
                        (feature) => feature.primaryFeature)}\n` +
                    "primaryFeatureAutoGenerated = true\n" +
                    "\n";

    if (databaseConnection.selectionTables.trim().length > 0 ||
        databaseConnection.selectionFilter.trim().length > 0) {
      databaseFile += "# Optional additional constraints to retrieve a subset of the table.\n";
      if (databaseConnection.selectionTables.trim().length > 0) {
        databaseFile += `selectionTables = ${databaseConnection.selectionTables}\n`;
      }
      if (databaseConnection.selectionFilter.trim().length > 0) {
        databaseFile += `selectionFilter = ${databaseConnection.selectionFilter}\n`;
      }
    }

    databaseFile += "\n" +
                    "# Optional maximum cache size (expressed as number of objects, default is 100).\n" +
                    `maxCacheSize = ${databaseConnection.maxCacheSize}\n` +
                    "\n";

    if (databaseConnection.alwaysCloseConnection) {
      databaseFile += "# Optionally close the database connection after each query.\n" +
                      "# Only advisable when using a connection pooling database driver.\n" +
                      "alwaysCloseConnection = true\n";
    }

    databaseFile += "\n" +
                    "# Additional properties required for creating a new Database table,\n" +
                    "# for instance with the sample\n" +
                    "#   encoder.postgresql.bat resources/Data/Shp/Usa/cities.shp resources/Data/Postgresql/cities.pgs\n\n";

    databaseFile += `SRID = ${databaseConnection.serverDefaultSrs}\n`;

    if (databaseConnection.bounds.length > 0) {
      databaseFile += `bounds = ${databaseConnection.bounds}\n`;
    }
    databaseFile += "\n";
    if (databaseConnection.geometryType.length > 0) {
      databaseFile += "# Optional geometry type of the geometry column.\n" +
                      `geometryType = ${databaseConnection.geometryType}\n` +
                      "\n";
    }
    databaseFile += "# The regular SQL data types of the feature columns.\n";
    for (let i = 0; i < databaseConnection.databaseFeatures.length; i++) {
      databaseFile += `featureDataTypes.${i} = ${databaseConnection.databaseFeatures[i].dataType}\n`;
    }
    databaseFile += "\n" +
                    "# Careful with this option: it will drop the existing table (if any) when\n" +
                    "# creating a new table (instead of failing with an SQL exception).\n";
    databaseFile += `dropBeforeCreatingTable = ${databaseConnection.dropBeforeCreatingTable.toString()}\n`;
    return databaseFile;
  }

  private getOtherOptionsState(): OtherOptionsState {
    const {addType, path, initialPath} = this.state;
    const otherOptions: OtherOptionsState = {addType};
    if (path || initialPath) {
      otherOptions["initialPath"] = path || initialPath;
    }
    return otherOptions;
  }

  private getAdvancedOptionsState(): AdvancedOptionsState {
    return {
      crawlReplicateDirStructure: this.state.crawlReplicateDirStructure,
      crawlOnlySelectedExtensions: this.state.crawlOnlySelectedExtensions,
      crawlFiltersActive: this.state.crawlFiltersActive,
      crawlGeneratePyramids: this.state.crawlGeneratePyramids,
      crawlGenerateThumbnail: this.state.crawlGenerateThumbnail,
      crawlMetadataParsers: this.state.crawlMetadataParsers,
      crawlMetadataParserStopAfterFirstSuccessful: this.state.crawlMetadataParserStopAfterFirstSuccessful,
      crawlAllowDownloads: this.state.crawlAllowDownloads,
      unmappedRoles: this.state.unmappedRoles,
    };
  }

  private renderTitle() {
    const {isExecuting, isSuccess} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }
    return <Modal.Title><FormattedMessage id="studio.add-data-dialog.title"
                                          defaultMessage="Add Data"/></Modal.Title>;
  }

  private renderButtons() {
    const {isExecuting, isSuccess, path, addType, files, s3Path} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }
    if (addType === AddType.UPLOAD) {
      return (
          <div>
            <Button onClick={this.closeDialog}>
              <FormattedMessage id="studio.ui.add-data-dialog.close" defaultMessage="Close"/>
            </Button>
          </div>
      );
    } else {
      return (
          <div>
            <Button disabled={(addType === AddType.DATA_ROOT && (!path || path === "")) ||
                              (addType === AddType.FILES && (files.length === 0)) ||
                              (addType === AddType.S3 && (!s3Path || !(/^[sS]3:\/\/.*\/.*\/.*/.exec(s3Path))))}
                    onClick={this.performAdd} data-cy="submit">
              <FormattedMessage id="studio.add-data-dialog.confirm-button" defaultMessage="Add Data"/>
            </Button>

            <Button onClick={this.closeDialog}>
              <FormattedMessage id="studio.ui.add-data-dialog.cancel" defaultMessage="Cancel"/>
            </Button>
          </div>
      );
    }
  }

  private renderExecuting() {
    const {isExecuting} = this.state;
    if (isExecuting) {
      return (
          <div style={{textAlign: "center"}}>
            <h2><FormattedMessage id="studio.add-data-dialog.submitting" defaultMessage="Submitting add..."/></h2>
            <Spinner bigStyle/>
          </div>
      );
    }
    return null;
  }

  private renderError() {
    const {error} = this.state;

    if (error) {
      const errorMessage = mapErrorToMessage(error) as string;
      if (errorMessage) {
        return <Alert bsStyle="danger">{errorMessage}</Alert>;
      }
      return <ErrorDisplay error={error}/>;
    }
    return null;
  }

  updateParentDisplayName = () => {
    const {parentFolderId, api} = this.props;
    if (typeof this.props.parentFolderId === 'undefined' || this.state.parentFolderId === this.props.parentFolderId) {
      return
    }

    if (parentFolderId === null) {
      this.setState({
        parentFolderName: APOLLO_CATALOG_ROOT_FOLDER.title,
        parentFolderId
      });
    } else {
      api.getDataDetails(parentFolderId)
          .then((response) => {
            this.setState({
              parentFolderName: response.data.title,
              parentFolderId
            });
          })
          .catch((error) => {
            this.setState({
              error,
              parentFolderName: this.props.intl.formatMessage(MESSAGES.unknownParent),
              parentFolderId
            });
          });
    }
  }

  renderParent() {
    const {intl} = this.props;
    let parentFolderName: string;

    if (this.props.parentFolderId) {
      if (this.state.parentFolderName === "") {
        parentFolderName = this.props.parentFolderId;
      } else {
        parentFolderName = this.state.parentFolderName;
      }
    } else {
      parentFolderName = APOLLO_CATALOG_ROOT_FOLDER.title;
    }

    return <FormGroup>
      <Col componentClass={ControlLabel} sm={LABEL_COL_WIDTH}>
        <FormattedMessage
            id="studio.add-modal.selected-parent"
            defaultMessage="Selected Parent:"/>
      </Col>
      <Col sm={CONTROL_COL_WIDTH} style={{marginTop: "7px"}}>
        {parentFolderName}
      </Col>
      <Col sm={HELP_COL} className={"addDataDialog-Label"}>
        <OverlayTrigger placement="top" overlay={<Tooltip
            id="tooltip_help">
          <FormattedMessage id="studio.add-data.selected-parent.help"
                            defaultMessage="Data will be added to {parentFolderName} folder"
                            values={{parentFolderName}}
          />
        </Tooltip>}>
          <img src="theme/assets/images/help_16.png" id="data-type"
               alt={intl.formatMessage(MESSAGES.HELP)}/>
        </OverlayTrigger>
      </Col>
    </FormGroup>
  }

  private renderBody() {
    const {
      path,
      addType,
      databaseConnection,
      crawlFiltersAvailable,
      metadataParsersOptions,
      isExecuting
    } = this.state;
    const {api, intl} = this.props;
    const background = api.getPreviewBackground();

    if (isExecuting) {
      return this.renderExecuting();
    }

    return (
        <div>
          {this.renderError()}
          <Form horizontal>
            <FormGroup>
              <Col componentClass={ControlLabel} sm={LABEL_COL_WIDTH}>
                <FormattedMessage
                    id="studio.add-data-dialog.crawl-type"
                    defaultMessage="Type"/>
              </Col>
              <Col sm={CONTROL_COL_WIDTH}>
                <FormControl name="addType" value={addType} onChange={this.onChange} componentClass="select">
                  <option value={AddType.FILES}>
                    {intl.formatMessage(MESSAGES.addTypeFiles)}
                  </option>
                  <option value={AddType.DATA_ROOT}>
                    {intl.formatMessage(MESSAGES.addTypeDataRoot)}
                  </option>
                  <option value={AddType.UPLOAD}>
                    {intl.formatMessage(MESSAGES.addTypeUpload)}
                  </option>
                  <option value={AddType.DATABASE}>
                    {intl.formatMessage(MESSAGES.addTypeDatabase)}
                  </option>
                  <option value={AddType.OGC}>
                    {intl.formatMessage(MESSAGES.addTypeOGC)}
                  </option>
                  <option value={AddType.S3}>
                    {intl.formatMessage(MESSAGES.addTypeS3)}
                  </option>
                </FormControl>
              </Col>
              <Col sm={HELP_COL} className={"addDataDialog-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.ogc-connection.data-type.help"
                                    defaultMessage="Data type to add"/>
                </Tooltip>}>
                  <img src="theme/assets/images/help_16.png" id="data-type"
                       alt={intl.formatMessage(MESSAGES.HELP)}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            {this.renderParent()}

            {addType === AddType.DATA_ROOT && this.renderDirectoryChooser("path", path)}

            {addType === AddType.FILES && this.renderFileChooser()}

            {addType === AddType.DATABASE &&
             <React.Fragment>
               <DatabaseConnectionChooser onChange={this.onChange}/>
               <DatabaseConnectionForm
                   onRef={ref => this.databaseConnectionForm = ref}
                   key={`${databaseConnection.id}-${databaseConnection.type}`} databaseConnection={databaseConnection}
                   onChange={this.onChange} readOnly={databaseConnection.id !== null} forceSrsEditable={true}/>
             </React.Fragment>
            }

            {addType === AddType.UPLOAD &&
             <FileUploadForm initialFiles={this.state.droppedFiles}
                             uploadOptions={this.state.uploadOptions}
                             createFormData={defaultCreateFormData}
                             shouldCancel={false}
                             defaultCrawlSettings={this.applySecurityChanges}
                             onFileUploadSuccess={this.saveToLocalStorage}
             />
            }
            {addType === AddType.OGC &&
             <OgcConnectionForm onRef={ref => this.ogcConnectionForm = ref} onChange={this.onChange}/>
            }

            {addType === AddType.S3 &&
             <FormGroup>
               <Col componentClass={ControlLabel} sm={LABEL_COL_WIDTH}>
                 <FormattedMessage
                     id="studio.add-data-dialog.s3-path"
                     defaultMessage="S3 path"/>
               </Col>
               <Col sm={CONTROL_COL_WIDTH}>
                 <FormControl name="s3Path" value={this.state.s3Path} onChange={this.onChange}/>
               </Col>
               <Col sm={HELP_COL} className={"addDataDialog-Label"}>
                 <OverlayTrigger placement="top" overlay={<Tooltip
                     id="tooltip_help">
                   <FormattedMessage id="studio.add-data.s3-connection.s3-path.help"
                                     defaultMessage="s3 file location in the form of s3://<bucket-name>/<key-name>/<file name>"/>
                 </Tooltip>}>
                   <img src="theme/assets/images/help_16.png" id="s3Path"
                        alt={intl.formatMessage(MESSAGES.HELP)}/>
                 </OverlayTrigger>
               </Col>
             </FormGroup>
            }


            <Row>
              <Col sm={12}>
                <FormControlLabel
                    control={<Switch data-cy="show-advanced-options" name="showAdvancedOptions"
                                     checked={this.state.showAdvancedOptions} onChange={this.onChange}/>}
                    label={intl.formatMessage(MESSAGES.advancedOptions)}
                />
              </Col>
            </Row>
          </Form>

          {/* hack to ensure SecurityForm is rendered and do not lose its state, TODO: SecurityForm should use redux state instead */}
          <div style={{display: this.state.showAdvancedOptions ? "block" : "none"}}>
            <div style={{background: "white"}}>
              <Paper>
                <Tabs
                    value={this.state.activeTab}
                    onChange={this.onTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    scrollButtons="off"
                >
                  {ADVANCED_TABS.map((t) =>
                      <AddOptionsTab data-cy={t.value} key={t.value} label={t.label} value={t.value}
                                     style={{display: t.isHidden(this.state) ? "none" : null}}/>
                  )}
                </Tabs>
              </Paper>

              {/* Settings  */}
              <TabPanel value="settings" selectedValue={this.state.activeTab}>
                <Form horizontal>
                  <div hidden={addType !== AddType.DATA_ROOT}>
                    <FormGroup>
                      <Col sm={12}>
                        <Checkbox name="crawlReplicateDirStructure" checked={this.state.crawlReplicateDirStructure}
                                  onChange={this.onChange}>
                          <FormattedMessage id="studio.add-data-dialog.replicate-directory-structure"
                                            defaultMessage="Replicate directory structure"/>
                        </Checkbox>
                      </Col>
                    </FormGroup>
                    <FormGroup>
                      <Col sm={12}>
                        <Checkbox data-cy="crawl-only-selected-extensions" name="crawlOnlySelectedExtensions"
                                  checked={this.state.crawlOnlySelectedExtensions} onChange={this.onChange}>
                          <FormattedMessage id="studio.add-data-dialog.crawl-selected"
                                            defaultMessage="Crawl only selected extensions"/>
                        </Checkbox>
                        {this.state.crawlOnlySelectedExtensions && <Autocomplete
                            data-cy="extensions"
                            multiple
                            options={crawlFiltersAvailable}
                            value={this.state.crawlFiltersActive}
                            disableCloseOnSelect
                            onChange={(_event, value) => this.onChange({target: {name: "crawlFiltersActive", value}})}
                            renderOption={(option, {selected}) => (
                                <React.Fragment>
                                  <mui.Checkbox checked={selected}/>
                                  {option}
                                </React.Fragment>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined"
                                           placeholder={intl.formatMessage(MESSAGES.activeCrawlFilters)}/>
                            )}
                        />}
                      </Col>
                    </FormGroup>
                  </div>
                  <FormGroup>
                    <Col sm={12}>
                      <Checkbox name="crawlAllowDownloads" checked={this.state.crawlAllowDownloads}
                                onChange={this.onChange}>
                        <FormattedMessage id="studio.add-data-dialog.allow-downloads" defaultMessage="Allow downloads"/>
                      </Checkbox>
                    </Col>
                  </FormGroup>
                  <FormGroup>
                    <Col sm={12}>
                      <Checkbox name="crawlGenerateThumbnail" checked={this.state.crawlGenerateThumbnail}
                                onChange={this.onChange}>
                        <FormattedMessage id="studio.add-data-dialog.generate-thumbnails"
                                          defaultMessage="Generate Thumbnails"/>
                      </Checkbox>
                    </Col>
                  </FormGroup>
                  <FormGroup>
                    <Col sm={12}>
                      <Checkbox name="crawlGeneratePyramids" checked={this.state.crawlGeneratePyramids}
                                onChange={this.onChange}>
                        <FormattedMessage id="studio.add-data-dialog.generate-pyramids"
                                          defaultMessage="Generate Pyramids"/>
                      </Checkbox>
                    </Col>
                  </FormGroup>
                </Form>
              </TabPanel>

              {/* Security */}
              {/* hack to ensure SecurityForm is rendered and do not lose its state, TODO: SecurityForm should use redux state instead */}
              <div style={{display: this.state.activeTab === "security" ? "block" : "none"}}>
                <TabPanel value="security" selectedValue="security">
                  {this.state.isSecurityLoading && <div style={{textAlign: "center"}}>
                    <h2>{<FormattedMessage id="studio.add-data-dialog.loading" defaultMessage="Loading..."/>}</h2>
                    <Spinner bigStyle/>
                  </div>}

                  {!this.state.isSecurityLoading && <div style={{
                    display: (!this.state.mapDrawModeActive && !this.state.mapEditModeActive) ? "block" : "none",
                  }}>
                    <SecurityForm
                        ref={ref => this.securityForm = ref}
                        accessRules={this.state.crawlAccessRules}
                        isUserManagementActive={this.state.isUserManagementActive}
                        allRoles={this.state.allRoles}
                        dataType={null}
                        onDrawSpatialArea={this.onDrawSpatialArea}
                        onEditSpatialArea={this.onEditSpatialArea}
                        spatialAreaCoordinates={this.state.securitySpatialAreaCoordinates}
                        unmappedRoles={this.state.unmappedRoles}
                    />
                  </div>
                  }

                  <RIAMap style={{
                    width: "100%", height: "400px",
                    display: (this.state.mapDrawModeActive || this.state.mapEditModeActive) ? "block" : "none",
                  }}
                          background={background}
                          dataToVisualize={[]}
                          shouldFit={true}
                          shouldAnimateFit={true}
                          restrictNavigationBounds={background.bounds ? background.bounds : RESTRICT_NAVIGATION_BOUNDS}
                          shouldHideLayerTree={true}
                          shouldAddGridLayer={false}
                          showPreview={true}
                          drawModeActive={this.state.mapDrawModeActive}
                          editModeActive={this.state.mapEditModeActive}
                          onDrawCancel={this.onDrawCancel}
                          onEditCancel={this.onEditCancel}
                          editFeatureCoordinates={this.state.securitySpatialAreaCoordinates}
                          onFeatureDrawn={this.onFeatureDrawn}
                  />

                </TabPanel>
              </div>

              {/* Metadata */}
              <TabPanel value="metadata" selectedValue={this.state.activeTab}>
                <Form horizontal>
                  <Autocomplete
                      data-cy="parsers"
                      multiple
                      options={metadataParsersOptions}
                      value={this.state.crawlMetadataParsers}
                      disableCloseOnSelect
                      onChange={(_event, value) => this.onChange({target: {name: "crawlMetadataParsers", value}})}
                      renderOption={(option, {selected}) => (
                          <React.Fragment>
                            <mui.Checkbox checked={selected}/>
                            {option}
                          </React.Fragment>
                      )}
                      renderInput={(params) => (
                          <TextField {...params} variant="outlined"
                                     placeholder={intl.formatMessage(MESSAGES.parsers)}/>
                      )}
                  />

                  {this.renderMetadataParsersList()}

                  <Radio name="crawlMetadataParserStopAfterFirstSuccessful"
                         checked={this.state.crawlMetadataParserStopAfterFirstSuccessful === true}
                         onChange={(e) => this.onChange(e, true)}>
                    <FormattedMessage id="studio.add-data-dialog.first-successful" defaultMessage="First Successful"/>
                  </Radio>
                  <Radio name="crawlMetadataParserStopAfterFirstSuccessful"
                         checked={this.state.crawlMetadataParserStopAfterFirstSuccessful === false}
                         onChange={(e) => this.onChange(e, false)}>
                    <FormattedMessage id="studio.add-data-dialog.execute-all" defaultMessage="Execute All"/>
                  </Radio>
                </Form>
              </TabPanel>
            </div>
          </div>


          {(this.state.showAdvancedOptions || this.state.defaultsOptions !== null) &&
           <Checkbox name="setAsDefaults" checked={this.state.setAsDefaults} onChange={this.onChange}>
             <FormattedMessage id="studio.add-data-dialog.set-options-as-defaults"
                               defaultMessage="Set options as defaults"/>
           </Checkbox>}
        </div>
    );
  }

  private onDrawSpatialArea = () => {
    this.mapInit = true;
    this.setState(prevState => ({
      mapDrawModeActive: true,
      securitySpatialAreaCoordinates: [],
      prevSecuritySpatialAreaCoordinates: prevState.securitySpatialAreaCoordinates
    }));
  }

  private onEditSpatialArea = (coordinates: number[]) => {
    if (!this.mapInit) {
      this.mapInit = true;
      this.setState({mapEditModeActive: true, securitySpatialAreaCoordinates: coordinates});
      //This starts the edit map again if it is the first time launching.
      // If it is the first time starting the map closes when the initial polygon is drawn.
      setTimeout(() => {
        this.onEditSpatialArea(coordinates)
      }, 1000);
    } else {
      this.setState(prevState => ({
        mapEditModeActive: true,
        prevSecuritySpatialAreaCoordinates: prevState.securitySpatialAreaCoordinates,
        securitySpatialAreaCoordinates: coordinates,
      }));
    }
  }

  private onDrawCancel = () => {
    this.setState(prevState => ({
      mapDrawModeActive: false,
      securitySpatialAreaCoordinates: prevState.prevSecuritySpatialAreaCoordinates
    }));
  }

  private onEditCancel = () => {
    this.setState(prevState => ({
      mapEditModeActive: false,
      securitySpatialAreaCoordinates: prevState.prevSecuritySpatialAreaCoordinates
    }));
  }

  public applySecurityChanges = (): NewDataRoot => {
    return this.getNewDataRoot(false);
  }

  private onFeatureDrawn = (feature: Feature): boolean => {
    this.setState({
      securitySpatialAreaCoordinates: getPolygonPointsFromFeature(feature),
      mapDrawModeActive: false,
      mapEditModeActive: false,
    });

    return true;
  }

  private renderFileChooser() {
    return <span>
      <FileList source={ChooseFileFormSourceType.LocalAndNetwork} files={this.state.files}
                onChange={this.onChange}/>
    </span>
  }

  private renderDirectoryChooser(name: string, value: string) {
    return (
        <span>
          <FormGroup>
          <Col componentClass={ControlLabel} sm={LABEL_COL_WIDTH}>
            <FormattedMessage id="studio.add-data-dialog.path" defaultMessage="Path"/>
          </Col>
          <Col sm={CONTROL_COL_WIDTH}>
            <InputGroup>
              <FormControl className={"browse-data-input"} type="text" data-cy="path"
                           name={name} value={value} onChange={this.onChange}/>
              <InputGroupAddon className={"browse-data-button"} onClick={this.showChooseFolderDialog}>
                <InputGroupButton>
                  <LcdIcon icon="folder-open"/>
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
          </Col>
        </FormGroup>
        </span>
    )
  }

  private renderMetadataParsersList() {
    return <DragDropContext onDragEnd={this.onMetadataParsersDragEnd}>
      <Droppable droppableId="droppable" direction="vertical">
        {(providedDroppable) => (
            <div ref={providedDroppable.innerRef}>
              <List>
                {this.state.crawlMetadataParsers.map((item, index) => (
                    <Draggable key={item} draggableId={item} index={index}>
                      {(provided, snapshot) =>
                          this.renderMetadataParsersListItem(provided, snapshot, item)
                      }
                    </Draggable>
                ))}
                {providedDroppable.placeholder}
              </List>
            </div>
        )}
      </Droppable>
    </DragDropContext>;
  }

  private renderMetadataParsersListItem(provided: DraggableProvided, snapshot: DraggableStateSnapshot, name: string) {
    const listItem = <div ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={AddDataDialogComponent.getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style,
                          )}><ListItem>
      <ListItemIcon>
        <DragHandleIcon/>
      </ListItemIcon>
      <ListItemText
          primary={name}
      />
      <ListItemSecondaryAction>
        <IconButton onClick={this.onMetadataParserRemove.bind(this, name)}>
          <HighlightOffIcon/>
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
    </div>;

    if (snapshot.isDragging) {
      return ReactDOM.createPortal(<List>{listItem}</List>, this.listItemPortalElement);
    }
    return listItem;
  }

  private onMetadataParserRemove = (parserName: string) => {
    const {crawlMetadataParsers} = this.state;

    this.setState({
      crawlMetadataParsers: crawlMetadataParsers.filter(p => p !== parserName),
    });
  }

  private static getItemStyle(isDragging: boolean, draggableStyle: DraggingStyle | NotDraggingStyle) {
    return {
      ...draggableStyle,
      ...(isDragging && {
        background: "rgb(235,235,235)",
      }),
    };
  }

  private onMetadataParsersDragEnd = (result: DropResult) => {
    if (typeof result.destination?.index !== "number") {
      return;
    }

    this.setState(prevState => ({
      crawlMetadataParsers: AddDataDialogComponent.reorder(prevState.crawlMetadataParsers, result.source.index,
          result.destination.index),
    }));
  }

  private static reorder(list, startIndex, endIndex): any[] {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  }

  private renderModal() {
    const {isExecuting} = this.state;
    return (
        <Modal className="add-data-dialog" data-cy="add-data-dialog" show={true} onHide={this.closeDialog}
               backdrop="static">
          <Modal.Header closeButton={!isExecuting}>
            {this.renderTitle()}
          </Modal.Header>
          <Modal.Body>
            {this.renderBody()}
          </Modal.Body>
          <Modal.Footer>
            {this.renderButtons()}
          </Modal.Footer>
        </Modal>
    );
  }

  private renderChooseFolderDialog() {
    const {intl} = this.props;
    const {isChooseFolderDialogOpen, initialPath, addType} = this.state;
    return (
        <React.Fragment>
          {addType === AddType.DATA_ROOT && <ChooseFileDialog chooseMode={ChooseMode.FOLDERS}
                                                              source={ChooseFileFormSourceType.LocalAndNetwork}
                                                              handleChoose={this.handleChooseDataRoot}
                                                              show={isChooseFolderDialogOpen}
                                                              onHide={this.closeChooseFileDialog}
                                                              onChoosed={this.closeChooseFileDialog}
                                                              initialPath={initialPath}
                                                              title={intl.formatMessage(
                                                                  MESSAGES.chooseFolderDialogTitle)}
                                                              buttonText={intl.formatMessage(
                                                                  MESSAGES.chooseFolderDialogButtonText)}
          />}
        </React.Fragment>
    );
  }

  private onTabChange = (_event: any, value: AdvancedTabPanelValue) => {
    this.setState({
      activeTab: value,
    });
  }

  private onChange = (event: any, forcedValue?: any) => {
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;
    value = forcedValue !== undefined ? forcedValue : value;
    this.setState({[name]: value} as any);
  }

}

export const AddDataDialog = connect(null, mapDispatchToProps)(WithApi(injectIntl(AddDataDialogComponent)));